// Continental US bounding box, mirrors the API check.
const CONTINENTAL_US_BOUNDS = {
    minLatitude: 24,
    maxLatitude: 50,
    minLongitude: -125,
    maxLongitude: -66,
} as const

// Approximate Texas polygon as [latitude, longitude] pairs.
const TEXAS_OUTLINE: readonly (readonly [number, number])[] = [
    [36.5, -103.04],
    [36.5, -100.0],
    [34.56, -100.0],
    [34.38, -99.4],
    [34.2, -99.0],
    [34.13, -98.0],
    [33.8, -97.0],
    [33.85, -96.0],
    [33.87, -95.0],
    [33.64, -94.48],
    [33.55, -94.04],
    [32.0, -94.04],
    [31.6, -93.83],
    [31.2, -93.6],
    [30.9, -93.55],
    [30.4, -93.7],
    [30.08, -93.71],
    [29.7, -93.85],
    [29.3, -93.85],
    [28.6, -95.0],
    [27.6, -96.5],
    [25.9, -97.0],
    [25.95, -97.14],
    [25.88, -97.5],
    [26.06, -98.2],
    [26.43, -99.1],
    [27.3, -99.5],
    [27.6, -99.52],
    [28.25, -100.3],
    [28.71, -100.51],
    [29.33, -100.93],
    [29.77, -101.4],
    [29.88, -102.3],
    [29.75, -102.7],
    [28.97, -103.15],
    [29.35, -104.0],
    [29.65, -104.5],
    [30.68, -105.0],
    [31.39, -106.0],
    [31.78, -106.53],
    [32.0, -106.62],
    [32.0, -103.06],
]

const NEIGHBOURING_STATES: readonly {
    name: string
    latitude: readonly [number, number]
    longitude: readonly [number, number]
}[] = [
    {name: 'Louisiana', latitude: [28.9, 33.02], longitude: [-94.05, -88.8]},
    {name: 'Arkansas', latitude: [33.0, 36.5], longitude: [-94.62, -89.64]},
    {name: 'Oklahoma', latitude: [33.6, 37.0], longitude: [-103.0, -94.43]},
    {name: 'New Mexico', latitude: [31.33, 37.0], longitude: [-109.05, -103.0]},
]

const US_STATES: Record<string, string> = {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    DC: 'District of Columbia',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming',
}

type Coordinate = number | null | undefined

export function isRealSite(latitude: Coordinate, longitude: Coordinate): boolean {
    return (
        latitude != null &&
        longitude != null &&
        latitude >= CONTINENTAL_US_BOUNDS.minLatitude &&
        latitude <= CONTINENTAL_US_BOUNDS.maxLatitude &&
        longitude >= CONTINENTAL_US_BOUNDS.minLongitude &&
        longitude <= CONTINENTAL_US_BOUNDS.maxLongitude
    )
}

export function inTexas(latitude: Coordinate, longitude: Coordinate): boolean {
    if (latitude == null || longitude == null) {
        return false
    }
    // Point-in-polygon by ray casting
    let inside = false
    TEXAS_OUTLINE.forEach(([cornerLatitude, cornerLongitude], index) => {
        const [previousLatitude, previousLongitude] = TEXAS_OUTLINE.at(index - 1)!
        if (
            cornerLatitude > latitude !== previousLatitude > latitude &&
            longitude <
                ((previousLongitude - cornerLongitude) * (latitude - cornerLatitude)) /
                    (previousLatitude - cornerLatitude) +
                    cornerLongitude
        ) {
            inside = !inside
        }
    })
    return inside
}

function parseStateFromAddressText(addressText: string | null | undefined): string | null {
    const parts = (addressText ?? '')
        .split(',')
        .map((part) => part.trim())
        .filter((part) => part && !/^(USA|US|United States)$/i.test(part))
    const match = /^([A-Za-z ]+?)(?:\s+\d{5}(?:-\d{4})?)?$/.exec(parts.at(-1) ?? '')
    if (!match) {
        return null
    }

    const named = match[1]!.trim()
    return (
        US_STATES[named.toUpperCase()] ??
        Object.values(US_STATES).find((state) => state.toLowerCase() === named.toLowerCase()) ??
        null
    )
}

function findNeighbouringStateForPoint(latitude: number, longitude: number): string | null {
    const state = NEIGHBOURING_STATES.find(
        (box) =>
            latitude >= box.latitude[0] &&
            latitude <= box.latitude[1] &&
            longitude >= box.longitude[0] &&
            longitude <= box.longitude[1],
    )
    return state?.name ?? null
}

export function outOfStateLabel(site: {
    latitude: Coordinate
    longitude: Coordinate
    addressText?: string | null
}): string | null {
    const addressState = parseStateFromAddressText(site.addressText)
    let state: string | null
    if (isRealSite(site.latitude, site.longitude)) {
        if (inTexas(site.latitude, site.longitude)) {
            return null
        }

        state =
            addressState && addressState !== 'Texas'
                ? addressState
                : findNeighbouringStateForPoint(site.latitude!, site.longitude!)
    } else if (addressState && addressState !== 'Texas') {
        state = addressState
    } else {
        return null
    }
    return state ? `Out of state (${state})` : 'Out of Texas'
}
